import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { DataSourceStatus } from '@/lib/graphql/generated/graphql';
import { useErrorToastHandler } from '@/lib/hooks/useErrorToastHandler';
import { useUploadCouponCodesCsv } from '@/lib/hooks/useUploadCouponCodesCsv';
import { Upload } from 'lucide-react';

type CSVImportCouponCodesButtonProps = {
  dealId: string;
  onUploadSuccess: () => Promise<void>;
  buttonText?: string;
};

export function CSVImportCouponCodesButton({
  dealId,
  onUploadSuccess,
  buttonText,
}: CSVImportCouponCodesButtonProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isFetchingLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const {
    addCouponCodesCsvDataSourceToDeal,
    errors,
    dataSourceStatus,
    domainError,
    isDomainError,
  } = useUploadCouponCodesCsv(dealId);

  useErrorToastHandler({
    domainError,
    isDomainError,
    error: errors ?? null,
    domainToast: {
      titleKey: 'general.error_import_csv',
      defaultMessageKey: 'general.error_import_csv_message',
    },
    genericToast: {
      titleKey: 'general.error_import_csv',
    },
  });

  useEffect(() => {
    // Prevent default drag and drop behavior on document
    const preventDefaults = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('dragover', preventDefaults);
    document.addEventListener('drop', preventDefaults);

    return () => {
      document.removeEventListener('dragover', preventDefaults);
      document.removeEventListener('drop', preventDefaults);
    };
  }, []);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsLoading(true);

    try {
      const result = await addCouponCodesCsvDataSourceToDeal(file);
      if (!result.success) {
        // Toast affiché par le useEffect qui écoute domainError
      }
    } finally {
      setIsLoading(false);
      event.target.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file || !file.name.endsWith('.csv')) {
      toast({
        title: 'Format incorrect',
        description: 'Veuillez fournir un fichier CSV',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = await addCouponCodesCsvDataSourceToDeal(file);
      if (!result.success) {
        // Toast affiché par le useEffect qui écoute domainError
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (dataSourceStatus === DataSourceStatus.Processed) {
      onUploadSuccess();
    }
  }, [dataSourceStatus, onUploadSuccess]);

  const isLoading =
    isFetchingLoading ||
    dataSourceStatus === DataSourceStatus.Pending ||
    dataSourceStatus === DataSourceStatus.Created ||
    dataSourceStatus === DataSourceStatus.Imported;

  return (
    <div
      className={`border-2 border-dashed ${
        isDragging ? 'border-primary bg-primary/5' : 'border-gray-300'
      } rounded-md p-6 text-center transition-colors`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Upload
        className={`mx-auto h-12 w-12 ${
          isDragging ? 'text-primary' : 'text-gray-400'
        }`}
      />
      <p className="mt-2 text-sm text-gray-500">
        {t('general.drag_drop_csv_file')}
      </p>
      <div className="relative">
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          disabled={isLoading}
        />
        <Button variant="outline" disabled={isLoading}>
          <Upload className="mr-2 h-4 w-4" />
          {isLoading
            ? t('general.importation_in_progress')
            : (buttonText ?? t('general.import_coupons_codes'))}
        </Button>
      </div>
    </div>
  );
}
